import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Plus, ReceiptText, DollarSign, Clock, Scale,
  ChevronRight, Trash2, AlertCircle, CheckCircle2, X,
  Banknote, FileText, ArrowDownCircle, Pencil,
} from 'lucide-react';
import {
  Card, Button, EmptyState, KPI, Input, Select, Modal, Label, StatusDropdown,
  SkeletonListRows,
} from '../components/ui';
import FirstVisitTips from '../components/onboarding/FirstVisitTips';
import { useFirm } from '../contexts/FirmContext';
import { Can, AccessDenied, usePermissions } from '../contexts/PermissionsContext';
import {
  listInvoices, createInvoice, updateInvoice, deleteInvoice,
  createInvoiceLineItems, deleteInvoiceLineItems, getInvoice,
  listContacts, listMatters,
  updateMatterBilling, recordInstallmentPayment, listMatterPayments,
  addCaseCharge, listCaseCharges, deletePayment, deleteCaseCharge,
  listAllMatterPayments, listAllCaseCharges,
} from '../lib/db';
import { formatMoney, formatMoneyCompact, formatDate, fullName } from '../lib/formatters';
import { useActivityLogger } from '../lib/useActivityLogger';
import { withTimeout } from '../lib/fetchWithTimeout';
import { nextInvoiceNumber } from '../lib/invoiceNumber';
import GhanaTaxPicker from '../components/GhanaTaxPicker';
import { GHANA_TAX_LEVIES } from '../lib/constants';
import toast from '../lib/toast';

// ── helpers ──────────────────────────────────────────────────────────────────
const today = () => new Date().toISOString().split('T')[0];
const dueIn30 = () => {
  const d = new Date(); d.setDate(d.getDate() + 30);
  return d.toISOString().split('T')[0];
};
const blank = (invoiceNumber = 'INV-') => ({
  invoice_number: invoiceNumber,
  client_id: '', matter_id: '',
  issue_date: today(), due_date: dueIn30(),
  status: 'draft', notes: '', terms: '',
  tax_rate: 0, discount_amount: 0,
});

const blankItem = () => ({
  _id: Math.random().toString(36).slice(2),
  item_type: 'flat_fee',
  description: '',
  quantity: 1,
  rate: '',
  amount: 0,
});

const LINE_ITEM_TYPES = [
  { value: 'flat_fee',   label: 'Flat Fee' },
  { value: 'time',       label: 'Time' },
  { value: 'expense',    label: 'Expense' },
  { value: 'adjustment', label: 'Adjustment' },
  { value: 'discount',   label: 'Discount' },
  { value: 'pro_bono',   label: 'Pro Bono' },
];

const STATUS_TONES = {
  draft: 'slate', sent: 'blue', viewed: 'indigo', partial: 'amber',
  paid: 'green', overdue: 'red', void: 'slate',
};
const INVOICE_STATUSES = Object.entries(STATUS_TONES).map(([value, tone]) => ({
  value, tone, label: value.charAt(0).toUpperCase() + value.slice(1),
}));

const FEE_ARRANGEMENTS = [
  { value: 'flat_fee',    label: 'Flat Fee' },
  { value: 'hourly',      label: 'Hourly' },
  { value: 'contingency', label: 'Contingency' },
  { value: 'retainer',    label: 'Retainer' },
  { value: 'pro_bono',    label: 'Pro Bono' },
];

const CHARGE_TYPES = [
  { value: 'disbursement', label: 'Disbursement' },
  { value: 'court_fee',    label: 'Court Fee' },
  { value: 'filing_fee',   label: 'Filing Fee' },
  { value: 'travel',       label: 'Travel' },
  { value: 'expert_fee',   label: 'Expert Fee' },
  { value: 'other',        label: 'Other' },
];

const PAYMENT_METHODS = [
  { value: 'cash',        label: 'Cash' },
  { value: 'check',       label: 'Cheque' },
  { value: 'ach',         label: 'Bank Transfer' },
  { value: 'credit_card', label: 'Credit / Debit Card' },
  { value: 'wire',        label: 'Wire Transfer' },
  { value: 'mobile',      label: 'Mobile Money' },
  { value: 'other',       label: 'Other' },
];

// ── Progress bar ──────────────────────────────────────────────────────────────
function ProgressBar({ pct, tone = 'green' }) {
  const colors = { green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500', blue: 'bg-blue-500' };
  return (
    <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
      <div className={`h-2 rounded-full transition-all ${colors[tone]}`} style={{ width: `${Math.min(pct, 100)}%` }} />
    </div>
  );
}

// ── Case billing detail modal ─────────────────────────────────────────────────
function CaseBillingModal({ matter, firmId, onClose, onUpdated, onNewInvoice,
  onPaymentAdded, onPaymentRemoved, onChargeAdded, onChargeRemoved }) {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [charges, setCharges] = useState([]);
  const [matterInvoices, setMatterInvoices] = useState([]);

  // Fee agreement form
  const [feeForm, setFeeForm] = useState({
    agreed_fee: matter.agreed_fee || '',
    fee_arrangement: matter.fee_arrangement || 'flat_fee',
  });
  const [savingFee, setSavingFee] = useState(false);
  const [feeSuccess, setFeeSuccess] = useState(false);

  // Installment form
  //
  // `idempotencyKey` is generated ONCE when the form opens and reset
  // only when it closes successfully. Same key on a retry → partial
  // UNIQUE index on payments(firm_id, idempotency_key) rejects the
  // duplicate, so a network blip / double-tap / accidental re-submit
  // can't double-charge a client. See migration 134.
  const [showPayForm, setShowPayForm] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', payment_date: today(), method: 'cash', payment_label: '', notes: '' });
  const [payIdempotencyKey, setPayIdempotencyKey] = useState('');
  const [savingPay, setSavingPay] = useState(false);

  const openPayForm = () => {
    // Cryptographically random UUID per intent. crypto.randomUUID is
    // available on all evergreen browsers; the fallback covers older
    // Android WebViews that still ship without it.
    const newKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
    setPayIdempotencyKey(newKey);
    setShowPayForm(true);
  };

  // Extra charge form
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [chargeForm, setChargeForm] = useState({ description: '', amount: '', charge_date: today(), charge_type: 'disbursement', notes: '' });
  const [savingCharge, setSavingCharge] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: c }, { data: inv }] = await withTimeout(Promise.all([
        listMatterPayments(matter.id),
        listCaseCharges(matter.id),
        listInvoices(firmId, { matterId: matter.id }),
      ]), 15000);
      setPayments(p || []);
      setCharges(c || []);
      setMatterInvoices(inv || []);
    } catch { /* leave prior state, don't hang */ }
    finally { setLoading(false); }
  }, [matter.id, firmId]);

  useEffect(() => { load(); }, [load]);

  // Derived totals
  const agreedFee   = Number(feeForm.agreed_fee) || 0;
  const totalPaid   = payments.reduce((s, p) => s + Number(p.amount), 0);
  const totalExtra  = charges.reduce((s, c) => s + Number(c.amount), 0);
  const totalOwed   = agreedFee + totalExtra;
  const balance     = totalOwed - totalPaid;
  const paidPct     = totalOwed > 0 ? (totalPaid / totalOwed) * 100 : 0;
  const barTone     = paidPct >= 100 ? 'green' : paidPct >= 50 ? 'amber' : 'red';

  const saveFee = async (e) => {
    e.preventDefault();
    setSavingFee(true);
    const { error } = await updateMatterBilling(matter.id, {
      agreed_fee: agreedFee || 0,
      fee_arrangement: feeForm.fee_arrangement,
    });
    setSavingFee(false);
    if (error) { toast.error(error.message); return; }
    setFeeSuccess(true);
    setTimeout(() => setFeeSuccess(false), 2000);
    onUpdated({ ...matter, agreed_fee: agreedFee, fee_arrangement: feeForm.fee_arrangement });
  };

  const savePayment = async (e) => {
    e.preventDefault();
    setSavingPay(true);
    const { data, error } = await recordInstallmentPayment(firmId, {
      matter_id: matter.id,
      client_id: matter.client_id || null,
      amount: Number(payForm.amount),
      payment_date: payForm.payment_date,
      method: payForm.method,
      payment_label: payForm.payment_label || null,
      notes: payForm.notes || null,
      // Stable across retries — the same key the form opened with.
      // Migration 134's partial UNIQUE index uses it to dedup.
      idempotency_key: payIdempotencyKey || null,
    });
    setSavingPay(false);
    if (error) {
      // 23505 = unique_violation. Means the same idempotency key was
      // already accepted (i.e. the FIRST attempt landed and this is a
      // retry). Surface that as success — the user got what they asked
      // for, just on an earlier attempt.
      if (error.code === '23505' || /idempotency/i.test(error.message || '')) {
        toast.success('Payment already recorded.');
        setPayForm({ amount: '', payment_date: today(), method: 'cash', payment_label: '', notes: '' });
        setShowPayForm(false);
        setPayIdempotencyKey('');
        load();
        return;
      }
      toast.error(error.message); return;
    }
    setPayments((prev) => [data, ...prev]);
    onPaymentAdded?.(matter.id, Number(payForm.amount));
    setPayForm({ amount: '', payment_date: today(), method: 'cash', payment_label: '', notes: '' });
    setShowPayForm(false);
    // Reset the key so a future "Record payment" gets a fresh one.
    setPayIdempotencyKey('');
  };

  const saveCharge = async (e) => {
    e.preventDefault();
    setSavingCharge(true);
    const { data, error } = await addCaseCharge(firmId, {
      matter_id: matter.id,
      client_id: matter.client_id || null,
      description: chargeForm.description,
      amount: Number(chargeForm.amount),
      charge_date: chargeForm.charge_date,
      charge_type: chargeForm.charge_type,
      notes: chargeForm.notes || null,
    });
    setSavingCharge(false);
    if (error) { toast.error(error.message); return; }
    setCharges((prev) => [data, ...prev]);
    onChargeAdded?.(matter.id, Number(chargeForm.amount));
    setChargeForm({ description: '', amount: '', charge_date: today(), charge_type: 'disbursement', notes: '' });
    setShowChargeForm(false);
  };

  const removePayment = async (id) => {
    if (!window.confirm('Remove this payment record?')) return;
    const p = payments.find((x) => x.id === id);
    await deletePayment(id);
    setPayments((prev) => prev.filter((p) => p.id !== id));
    if (p) onPaymentRemoved?.(matter.id, Number(p.amount));
  };

  const removeCharge = async (id) => {
    if (!window.confirm('Remove this charge?')) return;
    const c = charges.find((x) => x.id === id);
    await deleteCaseCharge(id);
    setCharges((prev) => prev.filter((c) => c.id !== id));
    if (c) onChargeRemoved?.(matter.id, Number(c.amount));
  };

  return (
    <Modal open title={`Case Billing — ${matter.title}`} onClose={onClose} size="lg">
      <div className="space-y-6">

        {/* ── Balance summary ────────────────────────────────────────────── */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="grid grid-cols-4 divide-x divide-slate-200 dark:divide-slate-700">
            {[
              { label: 'Agreed Fee',     value: formatMoney(agreedFee),  sub: FEE_ARRANGEMENTS.find(f => f.value === feeForm.fee_arrangement)?.label },
              { label: 'Extra Charges',  value: formatMoney(totalExtra), sub: `${charges.length} charge${charges.length !== 1 ? 's' : ''}` },
              { label: 'Total Paid',     value: formatMoney(totalPaid),  sub: `${payments.length} payment${payments.length !== 1 ? 's' : ''}`, tone: 'green' },
              { label: 'Balance Due',    value: formatMoney(Math.max(balance, 0)), sub: balance <= 0 ? 'Fully paid ✓' : 'Outstanding', tone: balance <= 0 ? 'green' : 'red' },
            ].map(({ label, value, sub, tone }) => (
              <div key={label} className="p-4 text-center">
                <p className="text-xs text-slate-500 mb-1">{label}</p>
                <p className={`text-lg font-bold ${tone === 'green' ? 'text-emerald-600' : tone === 'red' ? 'text-red-600' : 'text-slate-900 dark:text-white'}`}>
                  {value}
                </p>
                {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
              </div>
            ))}
          </div>
          {agreedFee > 0 && (
            <div className="px-4 pb-3 pt-1 bg-slate-50 dark:bg-slate-800/40">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Payment progress</span>
                <span>{Math.round(paidPct)}%</span>
              </div>
              <ProgressBar pct={paidPct} tone={barTone} />
            </div>
          )}
        </div>

        {/* ── Agreed fee setup ──────────────────────────────────────────── */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
            <Scale size={14} /> Fee Agreement
          </h3>
          <form onSubmit={saveFee} className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[140px]">
              <Label>Arrangement</Label>
              <Select
                value={feeForm.fee_arrangement}
                onChange={(e) => setFeeForm(f => ({ ...f, fee_arrangement: e.target.value }))}
                options={FEE_ARRANGEMENTS}
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label>Agreed Total (GHS)</Label>
              <Input
                type="number" min="0" step="0.01"
                value={feeForm.agreed_fee}
                onChange={(e) => setFeeForm(f => ({ ...f, agreed_fee: e.target.value }))}
                placeholder="0.00"
              />
            </div>
            <Button type="submit" variant="secondary" disabled={savingFee}>
              {feeSuccess ? <><CheckCircle2 size={14} className="text-emerald-500" /> Saved</> : savingFee ? 'Saving…' : 'Save Fee'}
            </Button>
          </form>
        </div>

        {/* ── Installment payments ──────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <Banknote size={14} /> Installment Payments
            </h3>
            <Button size="sm" icon={Plus} onClick={() => (showPayForm ? setShowPayForm(false) : openPayForm())}>
              Record payment
            </Button>
          </div>

          {showPayForm && (
            <form onSubmit={savePayment} className="mb-4 p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount (GHS)</Label>
                  <Input type="number" min="0.01" step="0.01" value={payForm.amount}
                    onChange={(e) => setPayForm(f => ({ ...f, amount: e.target.value }))}
                    placeholder="0.00" required />
                </div>
                <div>
                  <Label>Payment date</Label>
                  <Input type="date" value={payForm.payment_date}
                    onChange={(e) => setPayForm(f => ({ ...f, payment_date: e.target.value }))} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Method</Label>
                  <Select value={payForm.method}
                    onChange={(e) => setPayForm(f => ({ ...f, method: e.target.value }))}
                    options={PAYMENT_METHODS} />
                </div>
                <div>
                  <Label>Label (e.g. "1st instalment")</Label>
                  <Input value={payForm.payment_label}
                    onChange={(e) => setPayForm(f => ({ ...f, payment_label: e.target.value }))}
                    placeholder="Optional label" />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={payForm.notes}
                  onChange={(e) => setPayForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional notes" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="ghost" onClick={() => setShowPayForm(false)}>Cancel</Button>
                <Button type="submit" disabled={savingPay}>{savingPay ? 'Saving…' : 'Record payment'}</Button>
              </div>
            </form>
          )}

          {loading ? <p className="text-sm text-slate-400 py-2">Loading…</p>
            : payments.length === 0 ? (
              <p className="text-sm text-slate-400 py-3 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                No payments recorded yet
              </p>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                      <ArrowDownCircle size={15} className="text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                        {p.payment_label || PAYMENT_METHODS.find(m => m.value === p.method)?.label || p.method}
                      </p>
                      <p className="text-xs text-slate-400">{formatDate(p.payment_date)} · {PAYMENT_METHODS.find(m => m.value === p.method)?.label}</p>
                      {p.notes && <p className="text-xs text-slate-400 truncate">{p.notes}</p>}
                    </div>
                    <span className="font-semibold text-emerald-600 font-mono text-sm">{formatMoney(p.amount)}</span>
                    <button onClick={() => removePayment(p.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
        </div>

        {/* ── Extra charges ─────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <AlertCircle size={14} /> Extra Charges
            </h3>
            <Button size="sm" variant="secondary" icon={Plus} onClick={() => setShowChargeForm(v => !v)}>
              Add charge
            </Button>
          </div>

          {showChargeForm && (
            <form onSubmit={saveCharge} className="mb-4 p-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Input value={chargeForm.description}
                    onChange={(e) => setChargeForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="e.g. High Court filing fee" required />
                </div>
                <div>
                  <Label>Amount (GHS)</Label>
                  <Input type="number" min="0.01" step="0.01" value={chargeForm.amount}
                    onChange={(e) => setChargeForm(f => ({ ...f, amount: e.target.value }))}
                    placeholder="0.00" required />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={chargeForm.charge_date}
                    onChange={(e) => setChargeForm(f => ({ ...f, charge_date: e.target.value }))} required />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={chargeForm.charge_type}
                    onChange={(e) => setChargeForm(f => ({ ...f, charge_type: e.target.value }))}
                    options={CHARGE_TYPES} />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input value={chargeForm.notes}
                    onChange={(e) => setChargeForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Optional" />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="ghost" onClick={() => setShowChargeForm(false)}>Cancel</Button>
                <Button type="submit" variant="secondary" disabled={savingCharge}>{savingCharge ? 'Saving…' : 'Add charge'}</Button>
              </div>
            </form>
          )}

          {loading ? <p className="text-sm text-slate-400 py-2">Loading…</p>
            : charges.length === 0 ? (
              <p className="text-sm text-slate-400 py-3 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                No extra charges yet
              </p>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                {charges.map((c) => (
                  <div key={c.id} className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                      <FileText size={15} className="text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{c.description}</p>
                      <p className="text-xs text-slate-400">{formatDate(c.charge_date)} · {CHARGE_TYPES.find(t => t.value === c.charge_type)?.label}</p>
                      {c.notes && <p className="text-xs text-slate-400 truncate">{c.notes}</p>}
                    </div>
                    <span className="font-semibold text-amber-600 font-mono text-sm">+{formatMoney(c.amount)}</span>
                    <button onClick={() => removeCharge(c.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
        </div>

        {/* ── Linked invoices ────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <ReceiptText size={14} /> Invoices
            </h3>
            {onNewInvoice && (
              <Button size="sm" variant="secondary" icon={Plus} onClick={onNewInvoice}>
                New invoice
              </Button>
            )}
          </div>
          {loading ? <p className="text-sm text-slate-400 py-2">Loading…</p>
            : matterInvoices.length === 0 ? (
              <p className="text-sm text-slate-400 py-3 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                No invoices for this matter yet
              </p>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                {matterInvoices.map((inv) => (
                  <div key={inv.id} className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                      <ReceiptText size={15} className="text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{inv.invoice_number}</p>
                      <p className="text-xs text-slate-400">
                        Issued {formatDate(inv.issue_date)} · Due {formatDate(inv.due_date)}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                      inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                      inv.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>{inv.status}</span>
                    <span className="font-semibold font-mono text-sm text-slate-700 dark:text-slate-300">{formatMoney(inv.total || 0)}</span>
                  </div>
                ))}
              </div>
            )}
        </div>

      </div>
    </Modal>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function BillingPage() {
  return (
    <Can permission="billing.view" fallback={<AccessDenied />}>
      <BillingPageInner />
    </Can>
  );
}

function BillingPageInner() {
  const { firmId, refreshSignal } = useFirm();
  const log = useActivityLogger();
  const { can } = usePermissions();
  const canViewFirmFinances = can('finance.view_firm');
  const [tab, setTab] = useState('cases'); // 'cases' | 'invoices'

  // Invoices tab state
  const [invoices, setInvoices]     = useState([]);
  const [loadingInv, setLoadingInv] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ]                   = useState('');
  const [showNew, setShowNew]       = useState(false);
  const [form, setForm]             = useState(blank());
  const [lineItems, setLineItems]   = useState([blankItem()]);
  const [saving, setSaving]         = useState(false);
  const [selectedLevies,  setSelectedLevies]  = useState([]);
  const [customTaxRate,   setCustomTaxRate]   = useState(0);

  // Edit invoice state
  const [editInvoice, setEditInvoice]     = useState(null); // invoice being edited
  const [editForm, setEditForm]           = useState({});
  const [editLineItems, setEditLineItems] = useState([]);
  const [editSaving, setEditSaving]       = useState(false);
  const [editLoading, setEditLoading]     = useState(false);
  const [editSelectedLevies, setEditSelectedLevies] = useState([]);
  const [editCustomTaxRate,  setEditCustomTaxRate]  = useState(0);

  // Case billing tab state
  const [matters, setMatters]       = useState([]);
  const [contacts, setContacts]     = useState([]);
  const [loadingCases, setLoadingCases] = useState(true);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [matterQ, setMatterQ]       = useState('');
  // Aggregated payments/charges keyed by matter_id (for inline table display)
  const [paidByMatter, setPaidByMatter]     = useState({});   // matter_id -> total paid
  const [extraByMatter, setExtraByMatter]   = useState({});   // matter_id -> total extra charges
  // Inline edit state
  const [editingFee, setEditingFee]         = useState(null); // matter id being edited
  const [editFeeForm, setEditFeeForm]       = useState({ agreed_fee: '', fee_arrangement: 'flat_fee' });
  const [savingFeeInline, setSavingFeeInline] = useState(false);

  useEffect(() => {
    if (!firmId) { setLoadingInv(false); setLoadingCases(false); return; }
    (async () => {
      try {
        const [{ data: inv }, { data: con }, { data: mat }, { data: pays }, { data: chgs }] = await withTimeout(Promise.all([
          listInvoices(firmId),
          listContacts(firmId),
          listMatters(firmId),
          listAllMatterPayments(firmId),
          listAllCaseCharges(firmId),
        ]), 20000);
        setInvoices(inv || []);
        setContacts(con || []);
        setMatters(mat || []);
        // Build aggregate maps by matter_id
        const paidMap = {};
        (pays || []).forEach((p) => {
          paidMap[p.matter_id] = (paidMap[p.matter_id] || 0) + Number(p.amount);
        });
        const extraMap = {};
        (chgs || []).forEach((c) => {
          extraMap[c.matter_id] = (extraMap[c.matter_id] || 0) + Number(c.amount);
        });
        setPaidByMatter(paidMap);
        setExtraByMatter(extraMap);
      } catch { /* timeout or error — leave empty state, don't hang */ }
      finally { setLoadingInv(false); setLoadingCases(false); }
    })();
  }, [firmId, refreshSignal]);

  // Invoices
  const openNew = () => { setForm(blank(nextInvoiceNumber(invoices))); setLineItems([blankItem()]); setSelectedLevies([]); setCustomTaxRate(0); setShowNew(true); };

  const updateLineItem = (id, field, value) => {
    setLineItems((prev) => prev.map((li) => {
      if (li._id !== id) return li;
      const updated = { ...li, [field]: value };
      if (field === 'quantity' || field === 'rate') {
        updated.amount = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.rate) || 0);
      }
      return updated;
    }));
  };
  const removeLineItem = (id) => setLineItems((prev) => prev.filter((li) => li._id !== id));

  const invoiceTotals = useMemo(() => {
    const subtotal = lineItems.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
    const tax_amount = subtotal * ((parseFloat(form.tax_rate) || 0) / 100);
    const discount = parseFloat(form.discount_amount) || 0;
    const total = subtotal + tax_amount - discount;
    return { subtotal, tax_amount, total };
  }, [lineItems, form.tax_rate, form.discount_amount]);

  const submitInvoice = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.warning('Please select a client.'); return; }
    setSaving(true);
    const { subtotal, tax_amount, total } = invoiceTotals;
    const { data, error } = await createInvoice(firmId, {
      ...form,
      client_id:       form.client_id || null,
      matter_id:       form.matter_id || null,
      tax_rate:        parseFloat(form.tax_rate) || 0,
      discount_amount: parseFloat(form.discount_amount) || 0,
      subtotal, tax_amount, total,
    });
    if (error) { setSaving(false); toast.error(error.message); return; }
    log('invoice', data.id, 'created', 'Invoice created');
    // Save line items
    const validItems = lineItems.filter((li) => li.description.trim());
    if (validItems.length > 0) {
      await createInvoiceLineItems(validItems.map(({ _id, ...li }, i) => ({
        invoice_id:  data.id,
        item_type:   li.item_type,
        description: li.description,
        quantity:    parseFloat(li.quantity) || 1,
        rate:        parseFloat(li.rate) || 0,
        amount:      parseFloat(li.amount) || 0,
        sort_order:  i,
      })));
    }
    setSaving(false);
    setInvoices((prev) => [{ ...data, subtotal, tax_amount, total }, ...prev]);
    setShowNew(false);
    setTab('invoices');
  };
  const changeInvoiceStatus = async (invoice, newStatus) => {
    const prev = invoices;
    setInvoices((inv) => inv.map((i) => i.id === invoice.id ? { ...i, status: newStatus } : i));
    const { error } = await updateInvoice(invoice.id, { status: newStatus });
    if (error) { toast.error(error.message); setInvoices(prev); return; }
    log('invoice', invoice.id, 'status_changed', `Invoice marked as ${newStatus}`);
  };

  const removeInvoice = async (invoice) => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_number}? This cannot be undone.`)) return;
    const { error } = await deleteInvoice(invoice.id);
    if (error) { toast.error(error.message); return; }
    setInvoices((prev) => prev.filter((i) => i.id !== invoice.id));
  };

  const openEdit = async (inv) => {
    setEditLoading(true);
    setEditInvoice(inv);
    const { data } = await getInvoice(inv.id);
    const existing = data?.line_items ?? [];
    setEditForm({
      invoice_number:  inv.invoice_number,
      status:          inv.status,
      client_id:       inv.client_id   || '',
      matter_id:       inv.matter_id   || '',
      issue_date:      inv.issue_date  || today(),
      due_date:        inv.due_date    || dueIn30(),
      tax_rate:        inv.tax_rate    ?? 0,
      discount_amount: inv.discount_amount ?? 0,
      notes:           inv.notes       || '',
      terms:           inv.terms       || '',
    });
    setEditLineItems(
      existing.length > 0
        ? existing.map((li) => ({ ...li, _id: li.id || Math.random().toString(36).slice(2) }))
        : [blankItem()]
    );
    // Reset levy pickers — stored rate is treated as custom when reopening an existing invoice
    setEditSelectedLevies([]);
    setEditCustomTaxRate(inv.tax_rate ?? 0);
    setEditLoading(false);
  };

  const updateEditLineItem = (id, field, value) => {
    setEditLineItems((prev) => prev.map((li) => {
      if (li._id !== id) return li;
      const updated = { ...li, [field]: value };
      if (field === 'quantity' || field === 'rate') {
        updated.amount = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.rate) || 0);
      }
      return updated;
    }));
  };
  const removeEditLineItem = (id) => setEditLineItems((prev) => prev.filter((li) => li._id !== id));

  const editTotals = useMemo(() => {
    const subtotal  = editLineItems.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
    const tax_amount = subtotal * ((parseFloat(editForm.tax_rate) || 0) / 100);
    const discount  = parseFloat(editForm.discount_amount) || 0;
    const total     = subtotal + tax_amount - discount;
    return { subtotal, tax_amount, total };
  }, [editLineItems, editForm.tax_rate, editForm.discount_amount]);

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.client_id) { toast.warning('Please select a client.'); return; }
    setEditSaving(true);
    const { subtotal, tax_amount, total } = editTotals;
    const { data, error } = await updateInvoice(editInvoice.id, {
      ...editForm,
      client_id:       editForm.client_id || null,
      matter_id:       editForm.matter_id || null,
      tax_rate:        parseFloat(editForm.tax_rate) || 0,
      discount_amount: parseFloat(editForm.discount_amount) || 0,
      subtotal, tax_amount, total,
    });
    if (error) { setEditSaving(false); toast.error(error.message); return; }
    // Replace line items
    await deleteInvoiceLineItems(editInvoice.id);
    const validItems = editLineItems.filter((li) => li.description?.trim());
    if (validItems.length > 0) {
      await createInvoiceLineItems(validItems.map(({ _id, id: _dbId, ...li }, i) => ({
        invoice_id:  editInvoice.id,
        item_type:   li.item_type,
        description: li.description,
        quantity:    parseFloat(li.quantity) || 1,
        rate:        parseFloat(li.rate) || 0,
        amount:      parseFloat(li.amount) || 0,
        sort_order:  i,
      })));
    }
    setEditSaving(false);
    setInvoices((prev) => prev.map((i) =>
      i.id === editInvoice.id ? { ...i, ...data, subtotal, tax_amount, total } : i
    ));
    setEditInvoice(null);
  };

  const filtered = useMemo(() => invoices
    .filter((i) => !statusFilter || i.status === statusFilter)
    .filter((i) => {
      if (!q) return true;
      const s = q.toLowerCase();
      return [i.invoice_number, fullName(i.client), i.client?.organization]
        .some((v) => (v || '').toLowerCase().includes(s));
    }), [invoices, statusFilter, q]);

  const kpis = useMemo(() => {
    const outstanding = invoices.filter((i) => ['sent','viewed','partial','overdue'].includes(i.status))
      .reduce((s, i) => s + Number(i.balance || 0), 0);
    const overdueCount = invoices.filter((i) => i.status === 'overdue').length;
    const paid   = invoices.filter((i) => i.status === 'paid').length;
    const draft  = invoices.filter((i) => i.status === 'draft').length;
    return { outstanding, overdueCount, paid, draft };
  }, [invoices]);

  // Case billing
  const filteredMatters = useMemo(() => matters.filter((m) => {
    if (!matterQ) return true;
    const s = matterQ.toLowerCase();
    const client = contacts.find((c) => c.id === m.client_id);
    return [m.title, m.matter_number, fullName(client)].some((v) => (v || '').toLowerCase().includes(s));
  }), [matters, matterQ, contacts]);

  const handleMatterUpdated = (updated) => {
    setMatters((prev) => prev.map((m) => m.id === updated.id ? { ...m, ...updated } : m));
  };

  // Called by CaseBillingModal when a payment is recorded, so table updates live
  const handlePaymentAdded = (matterId, amount) => {
    setPaidByMatter((prev) => ({ ...prev, [matterId]: (prev[matterId] || 0) + amount }));
  };
  // Called by CaseBillingModal when a charge is added
  const handleChargeAdded = (matterId, amount) => {
    setExtraByMatter((prev) => ({ ...prev, [matterId]: (prev[matterId] || 0) + amount }));
  };
  // Called by CaseBillingModal when a payment is deleted
  const handlePaymentRemoved = (matterId, amount) => {
    setPaidByMatter((prev) => ({ ...prev, [matterId]: Math.max((prev[matterId] || 0) - amount, 0) }));
  };
  // Called by CaseBillingModal when a charge is deleted
  const handleChargeRemoved = (matterId, amount) => {
    setExtraByMatter((prev) => ({ ...prev, [matterId]: Math.max((prev[matterId] || 0) - amount, 0) }));
  };

  // Case billing KPI totals
  const caseBillingKPIs = useMemo(() => {
    const totalAgreed   = matters.reduce((s, m) => s + (Number(m.agreed_fee) || 0), 0);
    const totalPaid     = Object.values(paidByMatter).reduce((s, v) => s + v, 0);
    const totalExtra    = Object.values(extraByMatter).reduce((s, v) => s + v, 0);
    const totalOwed     = totalAgreed + totalExtra;
    const totalBalance  = Math.max(totalOwed - totalPaid, 0);
    const fullyPaid     = matters.filter((m) => {
      const owed = (Number(m.agreed_fee) || 0) + (extraByMatter[m.id] || 0);
      return owed > 0 && (paidByMatter[m.id] || 0) >= owed;
    }).length;
    return { totalAgreed, totalPaid, totalExtra, totalBalance, fullyPaid };
  }, [matters, paidByMatter, extraByMatter]);

  const startEditFee = (m, e) => {
    e.stopPropagation();
    setEditingFee(m.id);
    setEditFeeForm({ agreed_fee: m.agreed_fee || '', fee_arrangement: m.fee_arrangement || 'flat_fee' });
  };

  const saveInlineFee = async (matterId) => {
    setSavingFeeInline(true);
    const { error } = await updateMatterBilling(matterId, {
      agreed_fee: Number(editFeeForm.agreed_fee) || 0,
      fee_arrangement: editFeeForm.fee_arrangement,
    });
    setSavingFeeInline(false);
    if (error) { toast.error(error.message); return; }
    setMatters((prev) => prev.map((m) => m.id === matterId
      ? { ...m, agreed_fee: editFeeForm.agreed_fee, fee_arrangement: editFeeForm.fee_arrangement }
      : m));
    setEditingFee(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Case fees, instalments, extra charges & invoices</p>
        </div>
        <div className="flex gap-2">
          {tab === 'invoices' && <Button variant="secondary" icon={Clock}>Generate from WIP</Button>}
          <Button icon={Plus} onClick={() => { openNew(); setTab('invoices'); }}>New invoice</Button>
        </div>
      </div>

      <FirstVisitTips
        pageKey="billing"
        title="How billing works in DocketFlow"
        tips={[
          'Every invoice and payment is attached to a matter — open the matter first if you don\'t see what you expect here.',
          'Generate invoices from unbilled time entries (or add line items manually). Tax and discount lines are configurable per invoice.',
          'Record payments here to clear an invoice — the receipt is automatically attached to the client\'s portal.',
        ]}
      />

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl w-fit">
        {[
          { key: 'cases',    label: 'Case Billing', icon: Scale },
          { key: 'invoices', label: 'Invoices',     icon: ReceiptText },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === key
                ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      {/* ── CASE BILLING TAB ────────────────────────────────────────────────── */}
      {tab === 'cases' && (
        <div className="space-y-4">
          {/* KPI cards — use compact money formatting so 5+ digit
              GHS amounts fit in the narrow grid-cols-2 (on mobile) /
              grid-cols-5 (on desktop) cards instead of being truncated
              to "GH₵380,0...". Full precision is in the hover tooltip
              via the KPI component's `title` prop. */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <KPI label="Total Agreed Fees"  value={formatMoneyCompact(caseBillingKPIs.totalAgreed)}  title={formatMoney(caseBillingKPIs.totalAgreed)}  icon={Scale}        tone="indigo" />
            <KPI label="Extra Charges"      value={formatMoneyCompact(caseBillingKPIs.totalExtra)}   title={formatMoney(caseBillingKPIs.totalExtra)}   icon={AlertCircle}  tone="amber" />
            <KPI label="Total Collected"    value={formatMoneyCompact(caseBillingKPIs.totalPaid)}    title={formatMoney(caseBillingKPIs.totalPaid)}    icon={Banknote}     tone="green" />
            <KPI label="Outstanding Balance" value={formatMoneyCompact(caseBillingKPIs.totalBalance)} title={formatMoney(caseBillingKPIs.totalBalance)} icon={DollarSign}  tone="red" />
            <KPI label="Fully Paid Cases"   value={caseBillingKPIs.fullyPaid}                                                                            icon={CheckCircle2} tone="green" />
          </div>

          <Card>
            <div className="p-4 border-b border-slate-100 dark:border-slate-800">
              <Input
                className="max-w-sm"
                placeholder="Search matters…"
                value={matterQ}
                onChange={(e) => setMatterQ(e.target.value)}
              />
            </div>

            {loadingCases ? (
              <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
            ) : filteredMatters.length === 0 ? (
              <EmptyState
                icon={Scale}
                title="Billing lives on case files"
                description="Every invoice, payment and trust transaction is attached to a matter. Open a case file first, then come back here to bill, record payments, and run the trust ledger."
                action={<Button onClick={() => { window.location.hash = '#/matters'; }}>Go to case files</Button>}
              />
            ) : (
              <>
                {/* Mobile cards */}
                <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredMatters.map((m) => {
                    const client    = contacts.find((c) => c.id === m.client_id);
                    const agreedFee = Number(m.agreed_fee) || 0;
                    const totalPaid = paidByMatter[m.id]  || 0;
                    const totalExtra= extraByMatter[m.id] || 0;
                    const totalOwed = agreedFee + totalExtra;
                    const balance   = Math.max(totalOwed - totalPaid, 0);
                    const paidPct   = totalOwed > 0 ? Math.min((totalPaid / totalOwed) * 100, 100) : 0;
                    const arr       = FEE_ARRANGEMENTS.find(f => f.value === (m.fee_arrangement || 'flat_fee'));
                    return (
                      <li key={m.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm truncate">{m.title}</p>
                            <p className="text-xs text-slate-400">{m.matter_number}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{fullName(client) || client?.organization || '—'}</p>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 mt-1">
                              {arr?.label || '—'}
                            </span>
                          </div>
                          <div className="text-right flex-shrink-0 space-y-0.5">
                            <p className="text-xs text-slate-400">Agreed: <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{agreedFee > 0 ? formatMoney(agreedFee) : 'Not set'}</span></p>
                            <p className="text-xs text-slate-400">Paid: <span className="font-mono text-emerald-600 font-semibold">{totalPaid > 0 ? formatMoney(totalPaid) : '—'}</span></p>
                            <p className="text-xs text-slate-400">Balance: <span className={`font-mono font-bold ${balance === 0 ? 'text-emerald-600' : balance < totalOwed * 0.3 ? 'text-amber-600' : 'text-red-600'}`}>{balance === 0 ? '✓ Paid' : formatMoney(balance)}</span></p>
                          </div>
                        </div>
                        {totalOwed > 0 && (
                          <div className="mt-2">
                            <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                              <div className={`h-1.5 rounded-full ${paidPct >= 100 ? 'bg-emerald-500' : paidPct >= 50 ? 'bg-amber-400' : paidPct > 0 ? 'bg-orange-400' : 'bg-slate-300'}`} style={{ width: `${paidPct}%` }} />
                            </div>
                            <p className="text-xs text-slate-400 text-right mt-0.5">{Math.round(paidPct)}%</p>
                          </div>
                        )}
                        <div className="flex justify-end mt-2">
                          <button onClick={() => setSelectedMatter(m)} className="text-xs text-blue-500 font-semibold hover:text-blue-700">Manage billing →</button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-x-auto">
                <table className="w-full min-w-[800px] text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium">Matter</th>
                    <th className="text-left px-5 py-3 font-medium">Client</th>
                    <th className="text-left px-5 py-3 font-medium">Arrangement</th>
                    <th className="text-right px-5 py-3 font-medium">Agreed Fee</th>
                    <th className="text-right px-5 py-3 font-medium">Extra Charges</th>
                    <th className="text-right px-5 py-3 font-medium">Total Paid</th>
                    <th className="text-right px-5 py-3 font-medium">Balance Due</th>
                    <th className="px-5 py-3 font-medium w-44">Progress</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredMatters.map((m) => {
                    const client    = contacts.find((c) => c.id === m.client_id);
                    const agreedFee = Number(m.agreed_fee) || 0;
                    const totalPaid = paidByMatter[m.id]  || 0;
                    const totalExtra= extraByMatter[m.id] || 0;
                    const totalOwed = agreedFee + totalExtra;
                    const balance   = Math.max(totalOwed - totalPaid, 0);
                    const paidPct   = totalOwed > 0 ? Math.min((totalPaid / totalOwed) * 100, 100) : 0;
                    const barTone   = paidPct >= 100 ? 'emerald' : paidPct >= 50 ? 'amber' : paidPct > 0 ? 'orange' : 'slate';
                    const arr       = FEE_ARRANGEMENTS.find(f => f.value === (m.fee_arrangement || 'flat_fee'));
                    const isEditing = editingFee === m.id;

                    return (
                      <tr key={m.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 group">
                        {/* Matter */}
                        <td className="px-5 py-4">
                          <p className="font-semibold text-slate-800 dark:text-slate-200">{m.title}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{m.matter_number}</p>
                        </td>

                        {/* Client */}
                        <td className="px-5 py-4 text-slate-600 dark:text-slate-400">
                          {fullName(client) || client?.organization || <span className="text-slate-300">—</span>}
                        </td>

                        {/* Arrangement — editable */}
                        <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                          {isEditing ? (
                            <Select
                              value={editFeeForm.fee_arrangement}
                              onChange={(e) => setEditFeeForm(f => ({ ...f, fee_arrangement: e.target.value }))}
                              options={FEE_ARRANGEMENTS}
                            />
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                              {arr?.label || '—'}
                            </span>
                          )}
                        </td>

                        {/* Agreed Fee — editable */}
                        <td className="px-5 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                          {isEditing ? (
                            <Input
                              type="number" min="0" step="0.01"
                              value={editFeeForm.agreed_fee}
                              onChange={(e) => setEditFeeForm(f => ({ ...f, agreed_fee: e.target.value }))}
                              className="w-32 text-right"
                              placeholder="0.00"
                              autoFocus
                            />
                          ) : (
                            <span className={`font-mono font-semibold ${agreedFee > 0 ? 'text-slate-800 dark:text-slate-200' : 'text-slate-400'}`}>
                              {agreedFee > 0 ? formatMoney(agreedFee) : 'Not set'}
                            </span>
                          )}
                        </td>

                        {/* Extra Charges */}
                        <td className="px-5 py-4 text-right">
                          <span className={`font-mono text-sm ${totalExtra > 0 ? 'text-amber-600 font-semibold' : 'text-slate-400'}`}>
                            {totalExtra > 0 ? formatMoney(totalExtra) : '—'}
                          </span>
                        </td>

                        {/* Total Paid */}
                        <td className="px-5 py-4 text-right">
                          <span className={`font-mono text-sm ${totalPaid > 0 ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>
                            {totalPaid > 0 ? formatMoney(totalPaid) : '—'}
                          </span>
                        </td>

                        {/* Balance Due */}
                        <td className="px-5 py-4 text-right">
                          {totalOwed > 0 ? (
                            <span className={`font-mono font-bold text-sm ${
                              balance === 0 ? 'text-emerald-600' :
                              balance < totalOwed * 0.3 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {balance === 0 ? '✓ Paid' : formatMoney(balance)}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </td>

                        {/* Progress bar */}
                        <td className="px-5 py-4">
                          {totalOwed > 0 ? (
                            <div className="space-y-1">
                              <div className="h-2.5 w-full rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                                <div
                                  className={`h-2.5 rounded-full transition-all ${
                                    barTone === 'emerald' ? 'bg-emerald-500' :
                                    barTone === 'amber'   ? 'bg-amber-400' :
                                    barTone === 'orange'  ? 'bg-orange-400' : 'bg-slate-300'
                                  }`}
                                  style={{ width: `${paidPct}%` }}
                                />
                              </div>
                              <p className="text-xs text-slate-500 text-right">{Math.round(paidPct)}%</p>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => startEditFee(m, e)}
                              className="text-xs text-blue-500 font-semibold hover:text-blue-700">
                              + Set fee
                            </button>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2 justify-end">
                            {isEditing ? (
                              <>
                                <Button size="sm" onClick={() => saveInlineFee(m.id)} disabled={savingFeeInline}>
                                  {savingFeeInline ? '…' : 'Save'}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingFee(null)}>
                                  <X size={13} />
                                </Button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={(e) => startEditFee(m, e)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"
                                  title="Edit fee">
                                  <FileText size={13} />
                                </button>
                                <button
                                  onClick={() => setSelectedMatter(m)}
                                  className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"
                                  title="Manage billing">
                                  <ChevronRight size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {/* ── INVOICES TAB ────────────────────────────────────────────────────── */}
      {tab === 'invoices' && (
        <div className="space-y-4">
          {canViewFirmFinances && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KPI label="AR outstanding" value={formatMoney(kpis.outstanding)} icon={DollarSign} tone="amber" />
              <KPI label="Overdue"         value={kpis.overdueCount}             icon={ReceiptText} tone="red" />
              <KPI label="Paid"            value={kpis.paid}                     tone="green" />
              <KPI label="Drafts"          value={kpis.draft}                    tone="indigo" />
            </div>
          )}

          <Card>
            <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-slate-100 dark:border-slate-800">
              <Input className="flex-1" placeholder="Search by invoice # or client…" value={q}
                onChange={(e) => setQ(e.target.value)} />
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} options={[
                { value: '', label: 'All statuses' },
                ...Object.keys(STATUS_TONES).map((s) => ({ value: s, label: s })),
              ]} />
            </div>
            {loadingInv ? <SkeletonListRows rows={6} />
              : !filtered.length ? (
                <EmptyState
                  icon={ReceiptText}
                  title="Bill your first matter"
                  description="Invoices pull in unbilled time and expenses for a matter, add tax/discount, and produce a PDF you can email or share with the client portal."
                  action={<Button icon={Plus} onClick={openNew}>New invoice</Button>}
                />
              ) : (
                <>
                  {/* Mobile cards */}
                  <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                    {filtered.map((inv) => (
                      <li key={inv.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm">{inv.invoice_number}</p>
                            <p className="text-xs text-slate-500">{fullName(inv.client) || inv.client?.organization || '—'}</p>
                            <p className="text-xs text-slate-400 mt-0.5">Issued {formatDate(inv.issue_date)} · Due {formatDate(inv.due_date)}</p>
                            <div className="mt-1 flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                              <StatusDropdown value={inv.status} options={INVOICE_STATUSES} onChange={(s) => changeInvoiceStatus(inv, s)} />
                              {inv.status === 'draft' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); changeInvoiceStatus(inv, 'sent'); }}
                                  className="text-xs px-2.5 py-1 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition"
                                >
                                  Send to client
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 space-y-0.5">
                            <p className="text-xs text-slate-400">Total: <span className="font-mono font-semibold">{formatMoney(inv.total || 0)}</span></p>
                            <p className="text-xs text-slate-400">Balance: <span className="font-mono font-bold text-red-600">{formatMoney(inv.balance || 0)}</span></p>
                            <div className="flex items-center gap-2 justify-end mt-1" onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => openEdit(inv)} className="text-slate-400 hover:text-blue-500"><Pencil size={13} /></button>
                              <button onClick={() => removeInvoice(inv)} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {/* Desktop table */}
                  <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                    <tr>
                      <th className="text-left px-6 py-2 font-medium">Invoice</th>
                      <th className="text-left px-6 py-2 font-medium">Client</th>
                      <th className="text-left px-6 py-2 font-medium">Issued</th>
                      <th className="text-left px-6 py-2 font-medium">Due</th>
                      <th className="text-right px-6 py-2 font-medium">Total</th>
                      <th className="text-right px-6 py-2 font-medium">Balance</th>
                      <th className="text-left px-6 py-2 font-medium">Status</th>
                      <th className="px-6 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filtered.map((inv) => (
                      <tr key={inv.id} className="hover:bg-slate-50 dark:hover:bg-slate-800 group">
                        <td className="px-6 py-3 font-medium">{inv.invoice_number}</td>
                        <td className="px-6 py-3">{fullName(inv.client) || inv.client?.organization || '—'}</td>
                        <td className="px-6 py-3 text-slate-500">{formatDate(inv.issue_date)}</td>
                        <td className="px-6 py-3 text-slate-500">{formatDate(inv.due_date)}</td>
                        <td className="px-6 py-3 text-right font-mono">{formatMoney(inv.total || 0)}</td>
                        <td className="px-6 py-3 text-right font-mono font-semibold">{formatMoney(inv.balance || 0)}</td>
                        <td className="px-6 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <StatusDropdown value={inv.status} options={INVOICE_STATUSES}
                              onChange={(s) => changeInvoiceStatus(inv, s)} />
                            {inv.status === 'draft' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); changeInvoiceStatus(inv, 'sent'); }}
                                className="text-xs px-2.5 py-1 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition whitespace-nowrap">
                                Send to client
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => openEdit(inv)}
                              className="text-slate-400 hover:text-blue-500 transition-colors"
                              title="Edit invoice">
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => removeInvoice(inv)}
                              className="text-slate-300 hover:text-red-500 transition-colors"
                              title="Delete invoice">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>
                </>
              )}
          </Card>
        </div>
      )}

      {/* ── New invoice modal ───────────────────────────────────────────────── */}
      {showNew && (
        <Modal open title="New invoice" onClose={() => setShowNew(false)} size="xl">
          <form onSubmit={submitInvoice} className="space-y-6">

            {/* ── Header info ── */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Invoice number</Label>
                <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} required />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  options={INVOICE_STATUSES.map(({ value, label }) => ({ value, label }))} />
              </div>
              <div>
                <Label>Client <span className="text-red-500">*</span></Label>
                <Select value={form.client_id}
                  onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, client_id: v, matter_id: '' })); }}
                  required
                  options={[{ value: '', label: 'Select a client…' },
                    ...contacts.map((c) => ({ value: c.id, label: fullName(c) || c.organization || c.email }))]} />
              </div>
              <div>
                <Label>Matter (optional)</Label>
                <Select value={form.matter_id}
                  onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, matter_id: v })); }}
                  options={[{ value: '', label: 'None' },
                    ...matters.filter((m) => !form.client_id || m.client_id === form.client_id)
                      .map((m) => ({ value: m.id, label: `${m.matter_number} — ${m.title}` }))]} />
              </div>
              <div>
                <Label>Issue date</Label>
                <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} required />
              </div>
              <div>
                <Label>Due date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} required />
              </div>
            </div>

            {/* ── Line items ── */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line items</Label>
                <button type="button"
                  onClick={() => setLineItems((p) => [...p, blankItem()])}
                  className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                  <Plus size={12} /> Add item
                </button>
              </div>

              <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                {/* Column headers */}
                <div className="grid text-xs font-semibold uppercase tracking-wide text-slate-400 bg-slate-50 dark:bg-slate-800/60 px-3 py-2"
                  style={{ gridTemplateColumns: '130px 1fr 70px 100px 90px 32px' }}>
                  <span>Type</span>
                  <span>Description</span>
                  <span className="text-right">Qty</span>
                  <span className="text-right">Rate (GHS)</span>
                  <span className="text-right">Amount</span>
                  <span />
                </div>

                {/* Rows */}
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {lineItems.map((li) => (
                    <div key={li._id} className="grid items-center gap-2 px-3 py-2"
                      style={{ gridTemplateColumns: '130px 1fr 70px 100px 90px 32px' }}>
                      <select
                        value={li.item_type}
                        onChange={(e) => updateLineItem(li._id, 'item_type', e.target.value)}
                        className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 py-1.5 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500">
                        {LINE_ITEM_TYPES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                      </select>
                      <input
                        value={li.description}
                        onChange={(e) => updateLineItem(li._id, 'description', e.target.value)}
                        placeholder="Description of service or item…"
                        className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 py-1.5 text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required />
                      <input
                        type="number" min="0.01" step="0.01"
                        value={li.quantity}
                        onChange={(e) => updateLineItem(li._id, 'quantity', e.target.value)}
                        className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 py-1.5 text-right text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <input
                        type="number" min="0" step="0.01"
                        value={li.rate}
                        onChange={(e) => updateLineItem(li._id, 'rate', e.target.value)}
                        placeholder="0.00"
                        className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 py-1.5 text-right text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <span className="text-right text-xs font-mono font-semibold text-slate-700 dark:text-slate-200 pr-1">
                        {formatMoney(li.amount)}
                      </span>
                      <button type="button" onClick={() => removeLineItem(li._id)}
                        className="text-slate-300 hover:text-red-500 transition-colors flex items-center justify-center">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Totals ── */}
            <div className="flex flex-col sm:flex-row gap-6 justify-between">
              {/* Ghana tax picker */}
              <div className="flex-1 max-w-sm">
                <GhanaTaxPicker
                  selectedLevies={selectedLevies}
                  customRate={customTaxRate}
                  subtotal={invoiceTotals.subtotal}
                  onChange={(levies, combined) => {
                    setSelectedLevies(levies);
                    setForm({ ...form, tax_rate: combined + (customTaxRate || 0) });
                  }}
                  onCustomRate={(v) => {
                    setCustomTaxRate(v);
                    const levyTotal = GHANA_TAX_LEVIES.filter((l) => selectedLevies.includes(l.value)).reduce((s, l) => s + l.rate, 0);
                    setForm({ ...form, tax_rate: levyTotal + v });
                  }}
                />
              </div>
              {/* Summary */}
              <div className="w-72 space-y-2 self-end">
                <div className="flex justify-between text-sm text-slate-500">
                  <span>Subtotal</span>
                  <span className="font-mono">{formatMoney(invoiceTotals.subtotal)}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-slate-500 gap-3">
                  <span className="shrink-0">Discount (GHS)</span>
                  <input type="number" min="0" step="0.01"
                    value={form.discount_amount}
                    onChange={(e) => setForm({ ...form, discount_amount: e.target.value })}
                    className="w-28 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 py-1 text-right text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                {invoiceTotals.tax_amount > 0 && (
                  <div className="flex justify-between text-sm text-slate-400">
                    <span>Tax ({parseFloat(form.tax_rate) || 0}%)</span>
                    <span className="font-mono">{formatMoney(invoiceTotals.tax_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold text-slate-900 dark:text-white border-t border-slate-200 dark:border-slate-700 pt-2 mt-1">
                  <span>Total</span>
                  <span className="font-mono">{formatMoney(invoiceTotals.total)}</span>
                </div>
              </div>
            </div>

            {/* ── Notes & terms ── */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Notes</Label>
                <textarea rows={2} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Visible to client on invoice…"
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 py-2 text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>
              <div>
                <Label>Payment terms</Label>
                <textarea rows={2} value={form.terms}
                  onChange={(e) => setForm({ ...form, terms: e.target.value })}
                  placeholder="e.g. Payment due within 30 days…"
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 py-2 text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
              <Button type="button" variant="secondary" onClick={() => setShowNew(false)}>Cancel</Button>
              <Button type="submit" icon={Plus} disabled={saving}>
                {saving ? 'Saving…' : `Create invoice · ${formatMoney(invoiceTotals.total)}`}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Edit invoice modal ──────────────────────────────────────────────── */}
      {editInvoice && (
        <Modal open title={`Edit Invoice — ${editInvoice.invoice_number}`} onClose={() => setEditInvoice(null)} size="xl">
          {editLoading ? (
            <div className="py-12 text-center text-sm text-slate-500">Loading invoice…</div>
          ) : (
            <form onSubmit={submitEdit} className="space-y-6">

              {/* ── Header info ── */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Invoice number</Label>
                  <Input value={editForm.invoice_number}
                    onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })} required />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    options={INVOICE_STATUSES.map(({ value, label }) => ({ value, label }))} />
                </div>
                <div>
                  <Label>Client <span className="text-red-500">*</span></Label>
                  <Select value={editForm.client_id}
                    onChange={(e) => { const v = e.target.value; setEditForm(prev => ({ ...prev, client_id: v, matter_id: '' })); }}
                    required
                    options={[{ value: '', label: 'Select a client…' },
                      ...contacts.map((c) => ({ value: c.id, label: fullName(c) || c.organization || c.email }))]} />
                </div>
                <div>
                  <Label>Matter (optional)</Label>
                  <Select value={editForm.matter_id}
                    onChange={(e) => { const v = e.target.value; setEditForm(prev => ({ ...prev, matter_id: v })); }}
                    options={[{ value: '', label: 'None' },
                      ...matters.filter((m) => !editForm.client_id || m.client_id === editForm.client_id)
                        .map((m) => ({ value: m.id, label: `${m.matter_number} — ${m.title}` }))]} />
                </div>
                <div>
                  <Label>Issue date</Label>
                  <Input type="date" value={editForm.issue_date}
                    onChange={(e) => setEditForm({ ...editForm, issue_date: e.target.value })} required />
                </div>
                <div>
                  <Label>Due date</Label>
                  <Input type="date" value={editForm.due_date}
                    onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} required />
                </div>
              </div>

              {/* ── Line items ── */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Line items</Label>
                  <button type="button"
                    onClick={() => setEditLineItems((p) => [...p, blankItem()])}
                    className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                    <Plus size={12} /> Add item
                  </button>
                </div>

                <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div className="grid text-xs font-semibold uppercase tracking-wide text-slate-400 bg-slate-50 dark:bg-slate-800/60 px-3 py-2"
                    style={{ gridTemplateColumns: '130px 1fr 70px 100px 90px 32px' }}>
                    <span>Type</span><span>Description</span>
                    <span className="text-right">Qty</span>
                    <span className="text-right">Rate (GHS)</span>
                    <span className="text-right">Amount</span>
                    <span />
                  </div>
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {editLineItems.map((li) => (
                      <div key={li._id} className="grid items-center gap-2 px-3 py-2"
                        style={{ gridTemplateColumns: '130px 1fr 70px 100px 90px 32px' }}>
                        <select value={li.item_type}
                          onChange={(e) => updateEditLineItem(li._id, 'item_type', e.target.value)}
                          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 py-1.5 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {LINE_ITEM_TYPES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                        </select>
                        <input value={li.description || ''}
                          onChange={(e) => updateEditLineItem(li._id, 'description', e.target.value)}
                          placeholder="Description of service or item…"
                          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 py-1.5 text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required />
                        <input type="number" min="0.01" step="0.01" value={li.quantity || 1}
                          onChange={(e) => updateEditLineItem(li._id, 'quantity', e.target.value)}
                          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 py-1.5 text-right text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <input type="number" min="0" step="0.01" value={li.rate || ''}
                          onChange={(e) => updateEditLineItem(li._id, 'rate', e.target.value)}
                          placeholder="0.00"
                          className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 py-1.5 text-right text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <span className="text-right text-xs font-mono font-semibold text-slate-700 dark:text-slate-200 pr-1">
                          {formatMoney(li.amount || 0)}
                        </span>
                        <button type="button" onClick={() => removeEditLineItem(li._id)}
                          className="text-slate-300 hover:text-red-500 transition-colors flex items-center justify-center">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Totals ── */}
              <div className="flex flex-col sm:flex-row gap-6 justify-between">
                <div className="flex-1 max-w-sm">
                  <GhanaTaxPicker
                    selectedLevies={editSelectedLevies}
                    customRate={editCustomTaxRate}
                    subtotal={editTotals.subtotal}
                    onChange={(levies, combined) => {
                      setEditSelectedLevies(levies);
                      setEditForm({ ...editForm, tax_rate: combined + (editCustomTaxRate || 0) });
                    }}
                    onCustomRate={(v) => {
                      setEditCustomTaxRate(v);
                      const levyTotal = GHANA_TAX_LEVIES.filter((l) => editSelectedLevies.includes(l.value)).reduce((s, l) => s + l.rate, 0);
                      setEditForm({ ...editForm, tax_rate: levyTotal + v });
                    }}
                  />
                </div>
                <div className="w-72 space-y-2 self-end">
                  <div className="flex justify-between text-sm text-slate-500">
                    <span>Subtotal</span>
                    <span className="font-mono">{formatMoney(editTotals.subtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-slate-500 gap-3">
                    <span className="shrink-0">Discount (GHS)</span>
                    <input type="number" min="0" step="0.01" value={editForm.discount_amount || 0}
                      onChange={(e) => setEditForm({ ...editForm, discount_amount: e.target.value })}
                      className="w-28 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs px-2 py-1 text-right text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  {editTotals.tax_amount > 0 && (
                    <div className="flex justify-between text-sm text-slate-400">
                      <span>Tax ({parseFloat(editForm.tax_rate) || 0}%)</span>
                      <span className="font-mono">{formatMoney(editTotals.tax_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base font-bold text-slate-900 dark:text-white border-t border-slate-200 dark:border-slate-700 pt-2 mt-1">
                    <span>Total</span>
                    <span className="font-mono">{formatMoney(editTotals.total)}</span>
                  </div>
                </div>
              </div>

              {/* ── Notes & terms ── */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Notes</Label>
                  <textarea rows={2} value={editForm.notes || ''}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    placeholder="Visible to client on invoice…"
                    className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 py-2 text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <div>
                  <Label>Payment terms</Label>
                  <textarea rows={2} value={editForm.terms || ''}
                    onChange={(e) => setEditForm({ ...editForm, terms: e.target.value })}
                    placeholder="e.g. Payment due within 30 days…"
                    className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm px-3 py-2 text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <Button type="button" variant="secondary" onClick={() => setEditInvoice(null)}>Cancel</Button>
                <Button type="submit" icon={Pencil} disabled={editSaving}>
                  {editSaving ? 'Saving…' : `Save changes · ${formatMoney(editTotals.total)}`}
                </Button>
              </div>
            </form>
          )}
        </Modal>
      )}

      {/* ── Case billing modal ──────────────────────────────────────────────── */}
      {selectedMatter && (
        <CaseBillingModal
          matter={selectedMatter}
          firmId={firmId}
          onClose={() => setSelectedMatter(null)}
          onUpdated={handleMatterUpdated}
          onPaymentAdded={handlePaymentAdded}
          onPaymentRemoved={handlePaymentRemoved}
          onChargeAdded={handleChargeAdded}
          onChargeRemoved={handleChargeRemoved}
          onNewInvoice={() => {
            setSelectedMatter(null);
            setForm({ ...blank(nextInvoiceNumber(invoices)), matter_id: selectedMatter.id, client_id: selectedMatter.client_id || '' });
            setLineItems([blankItem()]);
            setShowNew(true);
            setTab('invoices');
          }}
        />
      )}
    </div>
  );
}
